import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { UserRole } from '@atendafilliados/shared'
import { api, apiGet, apiPost, ApiError } from '@/lib/api'

export interface AuthUser {
  id: string
  email: string
  name: string
  role: UserRole
  companyId: string | null
  avatarUrl?: string | null
}

const STORAGE_KEY = 'espelhacontas.auth'

interface PersistedAuth {
  token: string
  refreshToken: string
  user: AuthUser
  currentCompanyId: string | null
}

interface StoredAuth {
  data: PersistedAuth
  remember: boolean
}

function readPersisted(): StoredAuth | null {
  for (const [storage, remember] of [[localStorage, true], [sessionStorage, false]] as const) {
    try {
      const raw = storage.getItem(STORAGE_KEY)
      if (raw) return { data: JSON.parse(raw) as PersistedAuth, remember }
    } catch {
      // storage indisponível ou conteúdo inválido: tenta o próximo
    }
  }
  return null
}

function writePersisted(value: PersistedAuth | null, remember = true) {
  localStorage.removeItem(STORAGE_KEY)
  sessionStorage.removeItem(STORAGE_KEY)
  if (value) {
    const storage = remember ? localStorage : sessionStorage
    storage.setItem(STORAGE_KEY, JSON.stringify(value))
  }
}

export const useAuthStore = defineStore('auth', () => {
  const persisted = readPersisted()
  const token = ref<string | null>(persisted?.data.token ?? null)
  const refreshToken = ref<string | null>(persisted?.data.refreshToken ?? null)
  const user = ref<AuthUser | null>(persisted?.data.user ?? null)
  const currentCompanyId = ref<string | null>(persisted?.data.currentCompanyId ?? null)
  const rememberSession = ref(persisted?.remember ?? true)
  let refreshPromise: Promise<boolean> | null = null

  const isAuthenticated = computed(() => Boolean(token.value && user.value))
  const isSuperAdmin = computed(() => user.value?.role === UserRole.SUPER_ADMIN)
  const isCompanyUser = computed(() => Boolean(user.value?.companyId))

  function clearSession() {
    token.value = null
    refreshToken.value = null
    user.value = null
    currentCompanyId.value = null
    persist()
  }

  async function login(email: string, password: string, remember = true) {
    const res = await apiPost<{
      // Backend returns `accessToken` (not `token`). Field name MUST match.
      accessToken: string
      refreshToken: string
      expiresIn: number
      user: AuthUser
    }>('/api/auth/login', { email, password }, { noRetry: true })
    token.value = res.accessToken
    refreshToken.value = res.refreshToken
    user.value = res.user
    currentCompanyId.value = res.user.companyId
    rememberSession.value = remember
    persist()
    return res.user
  }

  async function register(payload: { name: string; email: string; password: string; companyName?: string; cpf: string; cnpj?: string; planId: string }) {
    const res = await apiPost<{
      accessToken: string
      refreshToken: string
      expiresIn: number
      user: AuthUser
    }>('/api/auth/register', payload, { noRetry: true })
    token.value = res.accessToken
    refreshToken.value = res.refreshToken
    user.value = res.user
    currentCompanyId.value = res.user.companyId
    persist()
    return res.user
  }

  async function refresh(): Promise<boolean> {
    if (refreshPromise) return refreshPromise
    if (!refreshToken.value) return false

    refreshPromise = (async () => {
      try {
        const res = await apiPost<{
        accessToken: string
        refreshToken?: string
        expiresIn?: number
      }>(
        '/api/auth/refresh',
        { refreshToken: refreshToken.value },
        { noRetry: true },
      )
        token.value = res.accessToken
        if (res.refreshToken) refreshToken.value = res.refreshToken
        if (res.user) user.value = res.user
        persist()
        return true
      } catch (err) {
        // Falha de rede não encerra a sessão. Só um refresh rejeitado pelo
        // servidor (revogado, inválido ou expirado) exige novo login.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) clearSession()
        return false
      }
    })()

    try {
      return await refreshPromise
    } finally {
      refreshPromise = null
    }
  }

  async function logout() {
    try {
      await apiPost('/api/auth/logout', undefined, { noRetry: true })
    } catch {
      /* ignore best-effort */
    }
    clearSession()
  }

  async function fetchProfile() {
    const profile = await api<AuthUser>('/api/users/me')
    user.value = profile
    if (!currentCompanyId.value) currentCompanyId.value = profile.companyId
    persist()
    return profile
  }

  /**
   * Garante que existe um `currentCompanyId` válido para o contexto atual:
   *   - Tenant user: `user.companyId` é obrigatório e já vem do token.
   *   - SUPER_ADMIN: se não houver empresa ativa no token, escolhe
   *     automaticamente a PRIMEIRA empresa disponível via `/api/companies`.
   *
   * Idempotente: se já houver `currentCompanyId`, retorna imediatamente.
   *
   * Retorna o `companyId` final ou `null` se a empresa não pode ser resolvida
   * (ex.: SUPER_ADMIN sem nenhuma empresa cadastrada no sistema).
   */
  async function ensureCompanyContext(): Promise<string | null> {
    if (currentCompanyId.value) return currentCompanyId.value

    if (!isSuperAdmin.value) return null // tenant sem companyId — falhar silenciosamente (login resolve)

    try {
      const res = await apiGet<{ data: Array<{ id: string }> } | Array<{ id: string }>>(
        '/api/companies',
        { pageSize: 1 },
      )
      const arr = Array.isArray(res) ? res : (res.data ?? [])
      const first = arr[0]?.id
      if (!first) return null
      setCurrentCompany(first)
      return first
    } catch (err) {
      // Não falhar — deixa null e a view mostra empty state apropriado.
      return null
    }
  }

  function setCurrentCompany(companyId: string) {
    currentCompanyId.value = companyId
    persist()
  }

  function persist() {
    if (token.value && refreshToken.value && user.value) {
      writePersisted({
        token: token.value,
        refreshToken: refreshToken.value,
        user: user.value,
        currentCompanyId: currentCompanyId.value,
      }, rememberSession.value)
    } else {
      writePersisted(null)
    }
  }

  return {
    token,
    refreshToken,
    user,
    currentCompanyId,
    isAuthenticated,
    isSuperAdmin,
    isCompanyUser,
    login,
    register,
    refresh,
    logout,
    fetchProfile,
    ensureCompanyContext,
    setCurrentCompany,
    clearSession,
  }
})
